import { FC } from 'react';
import { Box, Button, CircularProgress, Divider, Stack, TextField, Typography } from '@mui/material';
import { useOnboardingEmailModalContext } from '../OnboardingEmailModalContext';

const OnboardingEmailOutputPanel: FC = () => {
  const {
    draftEmail,
    draftSubject,
    setDraftSubject,
    draftBody,
    setDraftBody,
    flushDraftEmail,
    portalLink,
    generateEmail,
    isGenerating,
    canGenerate,
    isFollowUp,
    handleCopyEmail,
    handleCopyPortalLink,
    requestedDocuments,
  } = useOnboardingEmailModalContext();

  const requestCountLabel = (() => {
    if (requestedDocuments.length > 0) {
      const countLabel = `${requestedDocuments.length} item${
        requestedDocuments.length === 1 ? '' : 's'
      } will be requested`;
      return isFollowUp
        ? `${countLabel}. Provided documents and answered questions are excluded from the follow-up.`
        : countLabel;
    }
    if (isFollowUp) {
      return 'All selected items are already provided — regenerate to draft a follow-up.';
    }
    return 'Select documents that still need to be requested';
  })();

  const generateButtonLabel = (() => {
    if (isGenerating) {
      return 'Generating…';
    }
    return isFollowUp ? 'Regenerate follow-up email' : 'Generate onboarding email';
  })();

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {requestCountLabel}
      </Typography>
      <Button
        variant="contained"
        onClick={() => generateEmail()}
        disabled={!canGenerate}
        id="generate-onboarding-email-button"
      >
        {generateButtonLabel}
      </Button>
      {isGenerating && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'secondary.main' }}>
          <CircularProgress size={16} />
          <Typography variant="caption">
            {isFollowUp
              ? 'Updating document request and drafting follow-up email…'
              : 'Creating document request and drafting email…'}
          </Typography>
        </Box>
      )}
      <TextField
        label="Subject"
        value={draftSubject}
        onChange={event => setDraftSubject(event.target.value)}
        onBlur={flushDraftEmail}
        placeholder="Subject will appear here after generation…"
        fullWidth
        size="small"
        id="onboarding-email-subject"
      />
      <TextField
        label="Email"
        value={draftBody}
        onChange={event => setDraftBody(event.target.value)}
        onBlur={flushDraftEmail}
        placeholder="Email will appear here after generation…"
        multiline
        minRows={12}
        fullWidth
        id="onboarding-email-content"
      />
      <TextField
        label="Document request portal link"
        value={portalLink ?? ''}
        placeholder="Portal link will appear here after generation…"
        fullWidth
        size="small"
        InputProps={{ readOnly: true }}
        id="onboarding-email-portal-link"
      />
      <Divider />
      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          onClick={handleCopyEmail}
          disabled={!draftEmail}
          id="copy-onboarding-email-button"
        >
          Copy Email
        </Button>
        <Button
          variant="outlined"
          onClick={handleCopyPortalLink}
          disabled={!portalLink}
          id="copy-onboarding-portal-link-button"
        >
          Copy Link
        </Button>
      </Stack>
    </Stack>
  );
};

export default OnboardingEmailOutputPanel;
